import { BarChart3 } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import {
  useActivityReport,
  useAnomaliesReport,
  useTimeOffReport,
  useTimeReport,
  type ActivityReportGroup,
  type ReportKind,
  type TimeReportGroup,
} from "@/api/queries";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { AppsReportView } from "@/features/activity/AppsReportView";
import { NotFoundPage } from "@/features/system/NotFoundPage";
import { errorText } from "@/lib/errors";
import { cn } from "@/lib/utils";

import { ActivityReportView, TimeOffReportView } from "./ActivityReportView";
import { AnomalyReportView } from "./AnomalyReportView";
import { ExportButton } from "./ExportButton";
import { FiltersBar, useReportFilters } from "./filters";
import { GroupPicker, timeGroups, TimeReportView } from "./TimeReportView";

const tabs: { key: ReportKind; path: string }[] = [
  { key: "time", path: "" },
  { key: "activity", path: "activity" },
  { key: "apps", path: "apps" },
  { key: "time_off", path: "time-off" },
  { key: "anomalies", path: "anomalies" },
];

const activityModes = ["user", "date", "user,date"] as const;
type ActivityMode = (typeof activityModes)[number];

/** Reports: time, activity, applications and time off, with export. */
export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const { tab: tabPath = "" } = useParams();
  const tab = tabs.find((x) => x.path === tabPath);
  const filters = useReportFilters();
  const { scope, valid, staff, params, set } = filters;

  const timeGroup = parseGroups(params.get("group"), staff ? ["user", "project"] : ["project", "task"]);
  const activityMode: ActivityMode = (activityModes as readonly string[]).includes(params.get("view") ?? "")
    ? (params.get("view") as ActivityMode)
    : staff
      ? "user"
      : "date";
  const activityGroup = activityMode.split(",") as ActivityReportGroup[];

  const kind = tab?.key;
  const time = useTimeReport(scope, timeGroup, valid && kind === "time");
  const activity = useActivityReport(scope, activityGroup, valid && kind === "activity");
  const timeOff = useTimeOffReport(scope, valid && kind === "time_off");
  const anomalies = useAnomaliesReport(scope, valid && kind === "anomalies" && staff);

  // Anomalies are a manager's tool: an employee watching which of their days
  // were noticed would be learning how not to be noticed.
  if (!tab || (tab.key === "anomalies" && !staff)) return <NotFoundPage />;
  const groupBy = kind === "time" ? timeGroup : kind === "activity" ? activityGroup : undefined;

  return (
    <>
      <PageHeader
        title={t("nav.reports")}
        description={t("reports.subtitle")}
        actions={<ExportButton report={tab.key} scope={scope} groupBy={groupBy} disabled={!valid} />}
      />
      <nav aria-label={t("nav.reports")} className="-mt-2 mb-5 flex gap-1 overflow-x-auto border-b">
        {tabs
          .filter((x) => x.key !== "anomalies" || staff)
          .map((x) => (
            <Link
              key={x.key}
              to={{ pathname: x.path ? `/reports/${x.path}` : "/reports", search: shareable(params) }}
              aria-current={x.key === tab.key ? "page" : undefined}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 font-medium whitespace-nowrap transition-colors",
                x.key === tab.key
                  ? "border-primary text-primary"
                  : "border-transparent text-muted hover:text-fg",
              )}
            >
              {t(`reports.tabs.${x.key}`)}
            </Link>
          ))}
      </nav>
      <FiltersBar filters={filters} projects={kind === "time" || kind === "activity"} />

      {!valid ? (
        <Alert variant="warning">{t("apps.periodTooLong")}</Alert>
      ) : kind === "time" ? (
        <Loaded query={time}>
          {(data) => (
            <TimeReportView
              key={timeGroup.join(",")}
              data={data}
              lang={lang}
              today={filters.today}
              showPeople={staff}
              toolbar={<GroupPicker value={timeGroup} onChange={(g) => set({ group: g.join(",") })} />}
            />
          )}
        </Loaded>
      ) : kind === "activity" ? (
        <Loaded query={activity}>
          {(data) => (
            <ActivityReportView
              data={data}
              lang={lang}
              toolbar={
                staff && (
                  <Segmented
                    aria-label={t("reports.groupBy")}
                    value={activityMode}
                    onChange={(v) => set({ view: v })}
                    options={activityModes.map((m) => ({
                      value: m,
                      label: t(`reports.activity.modes.${m}`),
                    }))}
                  />
                )
              }
            />
          )}
        </Loaded>
      ) : kind === "apps" ? (
        <AppsReportView scope={scope} multiPeople={staff && scope.userIds?.length !== 1} />
      ) : kind === "anomalies" ? (
        <Loaded query={anomalies}>{(data) => <AnomalyReportView data={data} lang={lang} />}</Loaded>
      ) : (
        <Loaded query={timeOff}>{(data) => <TimeOffReportView data={data} lang={lang} />}</Loaded>
      )}
    </>
  );
}

/** Filters that make sense on every tab (grouping is per tab). */
function shareable(params: URLSearchParams): string {
  const p = new URLSearchParams(params);
  p.delete("group");
  p.delete("view");
  const s = p.toString();
  return s ? `?${s}` : "";
}

function parseGroups(raw: string | null, fallback: TimeReportGroup[]): TimeReportGroup[] {
  const list = (raw ?? "")
    .split(",")
    .filter(
      (g, i, all): g is TimeReportGroup => timeGroups.includes(g as TimeReportGroup) && all.indexOf(g) === i,
    )
    .slice(0, 3);
  return list.length > 0 ? list : fallback;
}

type LoadedProps<T> = {
  query: { data?: T; isError: boolean; error: unknown; refetch: () => unknown };
  children: (data: T) => ReactNode;
};

function Loaded<T>({ query, children }: LoadedProps<T>) {
  const { t } = useTranslation();
  if (query.isError) {
    return (
      <Card>
        <EmptyState
          icon={BarChart3}
          title={errorText(t, query.error)}
          action={
            <Button variant="secondary" onClick={() => void query.refetch()}>
              {t("common.retry")}
            </Button>
          }
        />
      </Card>
    );
  }
  if (!query.data) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  return <>{children(query.data)}</>;
}
